import { useMemo, type JSX } from "react";
import Ban from "@repo/icons/default/Ban";
import InformationCircle from "@repo/icons/default/InformationCircle";
import Left from "@repo/icons/default/Left";
import Money from "@repo/icons/default/Money";
import Trade from "@repo/icons/default/Trade";
import {
  createFileRoute,
  redirect,
  useLoaderData,
  useNavigate,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import { graphql } from "@repo/graphql-types";
import type { InsertRecipientTermsOfUseMutation } from "@repo/graphql-types/graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { PaymentTermsOfServiceItem } from "@/components/payment/payments-terms-of-service/payment-term-of-service-item";
import { Button } from "@/components/ui/button/button";
import { useGraphQLMutationWithErrorHandler, useGraphQL } from "@/hooks/use-graphql";
import { trackEvent } from "@/lib/tracking";
import { getIP } from "@/lib/ip-getter";
import { hasUserAcceptedOnlinePaymentTermsOfUse } from "@/lib/user-accepted-online-payment-terms-of-use";

const termsOfServicePageSearchSchema = z.object({
  appointmentId: z.number().optional(),
  patientId: z.number().optional(),
  origin: z.string().optional(),
  about: z.boolean().optional(),
  from: z.string().optional(),
});

const TermsOfServiceQuery = graphql(`
  query TermsOfServiceQuery($codUsuario: Int!, $codClinica: Int) {
    expensum_terms_of_use(where: { active: { _eq: true } }) {
      urlTerms
      id
      recipientTermsAgreed(where: {
        codUsuario: { _eq: $codUsuario }
      }) {
        id
      }
    }
    ExpensumGetRecipient(arg1: { codUsuario: $codUsuario, codClinica: $codClinica }) {
      recipient {
        id
      }
    }
  }
`);

const InsertRecipientTermsOfUse = graphql(/* GraphQL */ `
  mutation InsertRecipientTermsOfUse(
    $objects: [expensum_recipient_terms_of_use_agreed_insert_input!] = {}
  ) {
    insert_expensum_recipient_terms_of_use_agreed(objects: $objects) {
      affected_rows
    }
  }
`);

const PaymentTermsOfServicePage = (): JSX.Element => {
  const router = useRouter();
  const { flags } = useRouteContext({ from: "__root__" });

  const routerContext = useLoaderData({
    from: "/settings/payments/terms-of-service/",
  });

  const isRecipientAccountRegistrationActive = useMemo(
    () => flags["ativa-cadastro-de-conta-recebedora"],
    [flags],
  );

  const {
    user: { codUsuario, codClinica },
  } = useRouteContext({ from: "__root__" });

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    InsertRecipientTermsOfUse,
  );

  const navigate = useNavigate();

  const handleSubmit = async (): Promise<void> => {
    const ipAddress = await getIP();

    const onSuccess = (data: InsertRecipientTermsOfUseMutation): void => {
      trackEvent("Usuario Aceitou Termo", {
        codUsuario,
        codClinica,
        createdAt: new Date().toISOString(),
        agreedFromIp: ipAddress,
        type: "link-pagamento",
      });

      if (
        data.insert_expensum_recipient_terms_of_use_agreed &&
        data.insert_expensum_recipient_terms_of_use_agreed.affected_rows > 0
      ) {
        if (isRecipientAccountRegistrationActive && !recipientId) {
          navigateToCreateRecipient();
        } else {
          void navigate({
            to: "/settings/payments/create",
            search: {
              ...search,
              from:
                search.from === "appointment-created"
                  ? "appointment-created"
                  : "terms-of-service",
            },
          });
        }
      }
    };

    const object = {
      agreedAt: new Date().toISOString(),
      agreedFromIp: ipAddress,
      codClinica,
      codUsuario,
      recipientId,
      termsOfUseId,
    };

    if (userAlreadyAcceptTerms && isRecipientAccountRegistrationActive && !recipientId) {
      navigateToCreateRecipient();

      return;
    }

    void mutateAsync(
      {
        objects: [object],
      },
      { onSuccess },
    );
  };

  const backNavigationMap: Record<string, () => void> = {
    "appointment-created": () => {
      void router.navigate({ to: "/calendar" });
    },
    default: () => {
      router.history.back();
    },
  };

  const navigateToCreateRecipient = (): void => {
    void navigate({
      to: "/settings/payments/create-recipient",
      replace: true,
    });
  };

  const search = useSearch({ from: "/settings/payments/terms-of-service/" });

  const navigateBack = (): void => {
    backNavigationMap[search.from ?? "default"]();
  };

  const queryResult = useGraphQL(TermsOfServiceQuery, {
    codUsuario,
    codClinica,
  });
  const { data } = queryResult;

  const termsOfUseId = data?.expensum_terms_of_use[0]?.id;
  const termOfUseUrl = data?.expensum_terms_of_use[0]?.urlTerms ?? undefined;

  const recipientId = data?.ExpensumGetRecipient?.recipient?.id;

  const userAlreadyAcceptTerms =
    data?.expensum_terms_of_use[0]?.recipientTermsAgreed[0]?.id;

  return (
    <>
      <HeaderRoot>
        <HeaderButton onClick={navigateBack} icon={Left} align="start" />
        <HeaderTitle
          title={
            isRecipientAccountRegistrationActive ? "Cadastrar conta" : "Link de Pagamento"
          }
        />
      </HeaderRoot>

      <Page className="px-6">
        <h2 className="text-lg font-medium py-2" data-testid="page-title">
          Termos de Uso
        </h2>
        <p className="text-neutral-500 pb-6">
          O link de pagamento Livance permite cobrar seus pacientes de forma simples e
          digital. Para garantir segurança e clareza, destacamos abaixo as
          responsabilidades de uso dessa funcionalidade.
        </p>

        <div className="flex flex-col gap-6">
          <PaymentTermsOfServiceItem
            title="Responsabilidade pelas cobranças"
            Icon={Money}
            fillAsStroke
          >
            {" "}
            Você é responsável por definir o valor, descrever o serviço e enviar o link
          </PaymentTermsOfServiceItem>
          <PaymentTermsOfServiceItem title="Custos e Repasse" Icon={Trade}>
            {" "}
            O uso está sujeito a taxas de intermediação informadas previamente
          </PaymentTermsOfServiceItem>
          <PaymentTermsOfServiceItem
            title="Contestação do pagamento"
            Icon={InformationCircle}
          >
            {" "}
            Caso o pagamento seja contestado pelo paciente, o valor será descontado do seu
            repasse. Você poderá apresentar justificativa e contará com o suporte da
            Livance
          </PaymentTermsOfServiceItem>
          <PaymentTermsOfServiceItem title="Uso indevido e bloqueio" Icon={Ban}>
            {" "}
            O uso do link pode ser suspenso por uso fora das regras, alta taxa de
            contestações ou suspeita de fraude
          </PaymentTermsOfServiceItem>
        </div>

        <p className="text-sm text-neutral-500 py-6">
          Ao continuar, você declara que leu e aceita estes{" "}
          <a
            className="underline text-secondary-500"
            data-testid="user-agreement-link"
            href={termOfUseUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Termos de Uso
          </a>
          , que são parte integrante do contrato principal firmado com a Livance.
        </p>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          data-testid="continue-button"
          fullWidth
          disabled={!routerContext.memberIP}
          loading={isPending}
        >
          Continuar
        </Button>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/terms-of-service/")({
  component: PaymentTermsOfServicePage,
  validateSearch: termsOfServicePageSearchSchema,
  beforeLoad: async ({ context, search }) => {
    const hasUserAccepted = await hasUserAcceptedOnlinePaymentTermsOfUse(context);
    if (hasUserAccepted) {
      redirect({ to: "/settings/payments/create", throw: true, search });
    }
  },
  loader: async () => {
    const memberIP = await getIP();

    return { memberIP };
  },
});
